import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  arrayUnion,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import "./Inbox.css";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

export default function Inbox() {
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [avatars, setAvatars] = useState({});
  const [groupedInbox, setGroupedInbox] = useState({});
  const [groupedSent, setGroupedSent] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [replyText, setReplyText] = useState("");

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (usr) => setUser(usr));
    return unsub;
  }, [auth]);

  // Fetch current user's username & avatar
  useEffect(() => {
    if (!user?.uid) return;
    const userRef = doc(db, "user-data", user.uid);
    getDoc(userRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUsername(data.username || user.email);
          setUserAvatar(data.avatarUrl || "");
        } else {
          setUsername(user.email);
          setUserAvatar("");
        }
      })
      .catch(() => {
        setUsername(user.email);
        setUserAvatar("");
      });
  }, [user, db]);

  // Fetch inbox and sent messages
  useEffect(() => {
    if (!user?.uid) return;

    const inboxRef = collection(db, "ownerInbox", user.uid, "messages");
    const inboxQuery = query(inboxRef, orderBy("createdAt", "asc"));
    const unsubInbox = onSnapshot(inboxQuery, (snap) => {
      const messages = snap.docs
        .map((d) => ({ id: d.id, ...d.data(), ref: d.ref }))
        .filter((msg) => !msg.deletedBy?.includes(user.uid)); // ✅ filter deleted messages

      const grouped = {};
      messages.forEach((msg) => {
        if (!grouped[msg.senderId]) grouped[msg.senderId] = [];
        grouped[msg.senderId].push(msg);
      });
      setGroupedInbox(grouped);
      setLoading(false);

      messages.forEach(async (msg) => {
        if (msg.status === "sent" && msg.senderId !== user.uid) {
          await updateDoc(msg.ref, { status: "delivered" });
        }
      });

      Object.keys(grouped).forEach((uid) => {
        const msg = grouped[uid][0];
        if (msg.senderAvatar && !avatars[uid]) fetchAvatar(msg.senderAvatar, uid);
      });
    });

    const sentRef = collection(db, "sentMessages", user.uid, "messages");
    const sentQuery = query(sentRef, orderBy("createdAt", "asc"));
    const unsubSent = onSnapshot(sentQuery, (snap) => {
      const messages = snap.docs
        .map((d) => ({ id: d.id, ...d.data(), ref: d.ref }))
        .filter((msg) => !msg.deletedBy?.includes(user.uid)); // ✅ filter deleted messages

      const grouped = {};
      messages.forEach((msg) => {
        if (!grouped[msg.recipientId]) grouped[msg.recipientId] = [];
        grouped[msg.recipientId].push(msg);
      });
      setGroupedSent(grouped);
      setLoading(false);

      Object.keys(grouped).forEach((uid) => {
        const msg = grouped[uid][0];
        if (msg.senderAvatar && !avatars[uid]) fetchAvatar(msg.senderAvatar, uid);
      });
    });

    return () => {
      unsubInbox();
      unsubSent();
    };
  }, [user, db]);

  const fetchAvatar = async (path, uid) => {
    try {
      const url = await getDownloadURL(ref(storage, path));
      setAvatars((prev) => ({ ...prev, [uid]: url }));
    } catch (err) {
      console.error("Error fetching avatar for", uid, err);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedUserId) return;

    const messageData = {
      senderId: user.uid,
      senderName: username,
      senderAvatar: userAvatar,
      recipientId: selectedUserId,
      message: replyText.trim(),
      createdAt: serverTimestamp(),
      status: "sent",
    };

    try {
      await addDoc(collection(db, "ownerInbox", selectedUserId, "messages"), messageData);
      await addDoc(collection(db, "sentMessages", user.uid, "messages"), messageData);

      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_BASE}/sendNotification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetUserId: selectedUserId,
          title: `New message from ${username}`,
          body: replyText.trim(),
        }),
      });

      setReplyText("");
    } catch (err) {
      console.error("Error sending reply or notification:", err);
      alert("Failed to send message. Check Firestore rules and backend setup.");
    }
  };

  // ✅ Fixed deleteConversation using arrayUnion
  const deleteConversation = async (uid) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this conversation for yourself?"
    );
    if (!confirmDelete) return;

    try {
      const inboxSnap = await getDocs(collection(db, "ownerInbox", user.uid, "messages"));
      inboxSnap.forEach(async (docSnap) => {
        const data = docSnap.data();
        if (data.senderId === uid || data.recipientId === uid) {
          await updateDoc(docSnap.ref, { deletedBy: arrayUnion(user.uid) });
        }
      });

      const sentSnap = await getDocs(collection(db, "sentMessages", user.uid, "messages"));
      sentSnap.forEach(async (docSnap) => {
        const data = docSnap.data();
        if (data.senderId === uid || data.recipientId === uid) {
          await updateDoc(docSnap.ref, { deletedBy: arrayUnion(user.uid) });
        }
      });

      setGroupedInbox((prev) => {
        const newState = { ...prev };
        delete newState[uid];
        return newState;
      });
      setGroupedSent((prev) => {
        const newState = { ...prev };
        delete newState[uid];
        return newState;
      });

      if (selectedUserId === uid) setSelectedUserId(null);
    } catch (err) {
      console.error("Error deleting conversation:", err);
      alert("Failed to delete conversation. Try again.");
    }
  };

  if (!user) return <div className="inbox-loading">Please login to view your inbox.</div>;
  if (loading) return <div className="inbox-loading">Loading messages…</div>;

  const allUserIds = Array.from(
    new Set([...Object.keys(groupedInbox), ...Object.keys(groupedSent)])
  );

  const getMessagesForUser = (uid) =>
    [...(groupedInbox[uid] || []), ...(groupedSent[uid] || [])].sort(
      (a, b) => a.createdAt?.toDate?.() - b.createdAt?.toDate?.()
    );

  return (
    <div className="inbox-container">
      {/* Sidebar */}
      <div className="inbox-sidebar">
        <h2>Chats</h2>
        <div className="user-list">
          <p className="inbox-note">
            Please note: To manage storage efficiently, messages are automatically deleted at the end of each month.
          </p>
          {allUserIds.length === 0 && <div className="no-users">No conversations yet.</div>}
          {allUserIds.map((uid) => {
            const unreadMessages = (groupedInbox[uid] || []).filter(
              (msg) => msg.status !== "read" && msg.senderId === uid
            ).length;

            return (
              <div
                key={uid}
                className={`user-item ${uid === selectedUserId ? "active" : ""}`}
                onClick={async () => {
                  setSelectedUserId(uid);
                  const messagesToMark = groupedInbox[uid]?.filter(
                    (msg) => msg.status !== "read" && msg.senderId === uid
                  );
                  if (messagesToMark?.length) {
                    for (let msg of messagesToMark) {
                      await updateDoc(msg.ref, { status: "read" });
                    }
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="user-avatar">
                  {avatars[uid] ? (
                    <img
                      src={avatars[uid]}
                      alt="avatar"
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    />
                  ) : (
                    uid[0].toUpperCase()
                  )}
                </div>
                <div className="user-name">
                  {groupedInbox[uid]?.[0]?.senderName ||
                    groupedSent[uid]?.[0]?.senderName ||
                    uid}
                </div>

                {unreadMessages > 0 && <div className="unread-badge">{unreadMessages}</div>}

                <div
                  className="delete-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(uid);
                  }}
                >
                  <FiTrash2 size={16} color="#888" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat */}
      <div className="inbox-chat">
        {!selectedUserId && <div className="no-selection">Select a user to view messages</div>}
        {selectedUserId && (
          <div className="chat-messages">
            {getMessagesForUser(selectedUserId).map((msg) => {
              let tick = null;
              if (msg.senderId === user.uid) {
                if (msg.status === "sent") tick = "✓";
                else if (msg.status === "delivered") tick = "✓✓";
                else if (msg.status === "read") tick = <span style={{ color: "blue" }}>✓✓</span>;
              }
              return (
                <div
                  key={msg.id}
                  className={`chat-message ${msg.senderId === user.uid ? "sent" : "received"}`}
                >
                  <div className="message-content">{msg.message}</div>
                  <div className="message-meta">
                    <span className="sender-name">{msg.senderName || msg.senderEmail}</span>
                    <span className="timestamp">
                      {msg.createdAt?.toDate?.()?.toLocaleString()}
                    </span>
                    {tick && <span className="message-tick">{tick}</span>}
                  </div>
                </div>
              );
            })}

            <div className="reply-box">
              <input
                type="text"
                placeholder="Type your message..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReply()}
              />
              <button onClick={sendReply}>Reply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
